/**
 * 能链品牌通 - 数据获取层（契约层）
 *
 * 组件只依赖这里暴露的方法与返回结构，不关心数据来源。
 * - 油站选择器：品牌通油站列表 brandPass/getAllGasInfoList。
 * - 看板数据：品牌通运营报告详情 brandPass/reportDetail。
 * 本层负责把后端字段映射为组件契约结构（见 mapDashboard），页面与展示组件无需感知后端字段。
 */
import { getReportDetail, getAllGasInfoList } from '@/api/brandPass'
import { EMPTY } from './format'

// 子卡平台展示顺序：团油在前（团油图标）、高德在后（高德图标）
const PLATFORM_ORDER = { 团油: 0, 高德: 1 }
function platformRank (p) {
  return Object.prototype.hasOwnProperty.call(PLATFORM_ORDER, p) ? PLATFORM_ORDER[p] : 99
}
function orderByPlatform (list) {
  return (list || []).slice().sort((a, b) => platformRank(a.platform) - platformRank(b.platform))
}

function pad (n) {
  return n < 10 ? '0' + n : '' + n
}
// 兼容 'YYYY-MM-DDTHH:mm:ss' / 'YYYY-MM-DD HH:mm:ss'（规避 Safari 解析差异）
function parseDate (s) {
  if (!s) return null
  const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}
// 更新时间 -> 'YYYY.MM.DD HH:mm'
function fmtUpdate (s) {
  const d = parseDate(s)
  if (!d) return ''
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
// 时间段 -> 'YYYY-MM-DD HH:mm'；空值/非法返回 '--'
function fmtDateTime (s) {
  const d = parseDate(s)
  if (!d) return EMPTY
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
// 月份 '2026-06' -> '6月'
function fmtMonth (s) {
  const m = String(s || '').match(/^\d{4}-(\d{2})$/)
  return m ? `${parseInt(m[1], 10)}月` : (s || '')
}

// 种草码：状态快照(grassCode.xxx) + 月度趋势(grassCodeMonthTrend) -> 组件契约
// 始终返回结构：状态数缺失时为 null（由组件渲染成 '--'）；趋势数组为空时图表留空
function mapSeeding (status, trend, platform, keys) {
  const s = status || {}
  const list = trend || []
  return {
    platform,
    publishable: s.total,
    published: s.used,
    pending: s.wait,
    months: list.map(t => fmtMonth(t.month)),
    exposure: list.map(t => t[keys.exposure]),
    views: list.map(t => t[keys.view]),
    interaction: list.map(t => t[keys.interact])
  }
}

// 短信/PUSH：后端 [{platform,total,used,wait,exposure}] -> 组件契约（团油在前）
function mapReach (list) {
  return orderByPlatform(list).map(it => ({
    name: it.platform,
    totalSend: it.total,
    sentSend: it.used,
    pendingSend: it.wait,
    exposure: it.exposure
  }))
}

// 时间段类（3km置顶/品牌旗舰店）：后端 [{platform,startTime,endTime}] -> 组件契约（团油在前）
function mapRanges (list) {
  return orderByPlatform(list).map(it => ({
    name: it.platform,
    start: fmtDateTime(it.startTime),
    end: fmtDateTime(it.endTime)
  }))
}

// 后端报告详情 result -> 看板组件契约（见各展示组件 props）
// 各模块始终返回结构：字段缺失时为 null/'--'（由展示层占位），不再因数据缺失隐藏模块
function mapDashboard (result) {
  if (!result) result = {}
  const grass = result.grassCode || {}
  const trend = result.grassCodeMonthTrend || []
  const voice = result.voiceBroadcast || {}
  const ins = result.oilInsurance || {}
  return {
    // 顶部「更新于」展示报告时间 reportTime
    updateTime: fmtUpdate(result.reportTime),
    voice: { total: voice.total, done: voice.used, pending: voice.wait },
    seedingXhs: mapSeeding(grass.xiaohongshu, trend, '小红书', { exposure: 'xhsExposure', view: 'xhsView', interact: 'xhsInteract' }),
    seedingDouyin: mapSeeding(grass.douyin, trend, '抖音', { exposure: 'dyExposure', view: 'dyView', interact: 'dyInteract' }),
    sms: mapReach(result.sms),
    push: mapReach(result.push),
    top3km: mapRanges(result.top3km),
    flagship: mapRanges(result.flagshipStore),
    insurance: [{ name: '油品险', start: fmtDateTime(ins.startTime), end: fmtDateTime(ins.endTime) }]
  }
}

/**
 * 获取油站列表（油站选择器数据源）
 * @returns {Promise<Array<{gasId, gasName}>>}
 */
export async function getBrandPassStations () {
  const { code, result } = await getAllGasInfoList()
  return code === 200 ? (result || []) : []
}

/**
 * 单站账号默认油站（取列表首项）
 * @returns {Promise<{gasId, gasName}>}
 */
export async function getDefaultStation () {
  const list = await getBrandPassStations()
  return list[0] || { gasId: '', gasName: '' }
}

/**
 * 获取某油站的品牌通看板数据
 * @param {{gasId: string}} params
 * @returns {Promise<Object>} 见 mapDashboard 返回结构
 */
export async function getBrandPassData ({ gasId } = {}) {
  const { code, result } = await getReportDetail({ gasId })
  return code === 200 ? mapDashboard(result) : {}
}
